import {
  createFrontendPlugin,
  createApiFactory,
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  microsoftAuthApiRef,
  githubAuthApiRef,
  identityApiRef,
  ApiBlueprint,
  PageBlueprint,
  SubPageBlueprint,
  NavItemBlueprint,
  coreExtensionData,
} from '@backstage/frontend-plugin-api';
import { Routes, Route, Navigate } from 'react-router-dom';
import InventoryIcon from '@mui/icons-material/Inventory';
import AppsIcon from '@mui/icons-material/Apps';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { PluginQueryClientProvider } from './QueryClientProvider';

import {
  rootRouteRef,
  productListRouteRef,
  productDashboardRouteRef,
  productCreateRouteRef,
  tenantJoinRequestRouteRef,
  runtimeEnvironmentRouteRef,
  addRuntimeEnvironmentRouteRef,
  productEditRequestRouteRef,
  requestListRouteRef,
} from './routes';
import { bffApiRef, BffClient } from './api/bff/BffClient';
import {
  tenantInvitationApiRef,
  TenantInvitationClient,
} from './api/tenantInvitation/TenantInvitationClient';

export const page = PageBlueprint.makeWithOverrides({
  factory(originalFactory, { inputs }) {
    const pages = inputs.pages;
    return originalFactory({
      path: '/idp-control-base',
      title: 'IDP Control Base',
      noHeader: true,
      icon: <InventoryIcon />,
      routeRef: rootRouteRef,
      loader: async () => {
        // SubPageのタブの表示抑制のためにloaderをオーバーライドする。
        const firstPagePath = pages[0]?.get(coreExtensionData.routePath);
        const PageContent = () => (
          <PluginQueryClientProvider>
            <Routes>
              {firstPagePath && (
                <Route
                  index
                  element={<Navigate to={firstPagePath} replace />}
                />
              )}
              {pages.map((subPage, index) => (
                <Route
                  key={index}
                  path={`${subPage.get(coreExtensionData.routePath)}/*`}
                  element={subPage.get(coreExtensionData.reactElement)}
                />
              ))}
              {/* 旧 clz.scale Use Request のブックマーク救済 */}
              <Route
                path="clz-scale-use-request"
                element={<Navigate to="../product-create" replace />}
              />
            </Routes>
          </PluginQueryClientProvider>
        );
        return <PageContent />;
      },
    });
  },
});

export const navItemProducts = NavItemBlueprint.make({
  name: 'products',
  params: {
    title: 'Products',
    icon: AppsIcon,
    routeRef: productListRouteRef,
  },
});

export const navItemTenantJoinRequest = NavItemBlueprint.make({
  name: 'tenant-join-request',
  params: {
    title: 'clz.scale Use Request',
    icon: AssignmentIcon,
    routeRef: tenantJoinRequestRouteRef,
  },
});

/**
 * BFF API をプラグインに登録する ApiBlueprint。
 * BFF サービス経由でプロダクト情報等を取得する。
 */
export const bffApi = ApiBlueprint.make({
  name: 'bff',
  params: define =>
    define(
      createApiFactory({
        api: bffApiRef,
        deps: {
          configApi: configApiRef,
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
          microsoftAuthApi: microsoftAuthApiRef,
          githubAuthApi: githubAuthApiRef,
          identityApi: identityApiRef,
        },
        factory: ({
          configApi,
          discoveryApi,
          fetchApi,
          microsoftAuthApi,
          githubAuthApi,
          identityApi,
        }) => {
          const bffClientId = configApi.getOptionalString('bff.clientId') ?? '';
          const bffScope = `api://${bffClientId}/.default`;
          // auth.environment が 'development' のとき Microsoft 認証をスキップする
          const isDevelopment =
            configApi.getOptionalString('auth.environment') === 'development';
          return new BffClient({
            discoveryApi,
            fetchApi,
            microsoftAuthApi,
            githubAuthApi,
            identityApi,
            bffScope,
            isDevelopment,
          });
        },
      }),
    ),
});

/**
 * CEC テナント参加申請 API をプラグインに登録する ApiBlueprint。
 * Backstage backend の tenant-invitation プラグインを呼び出す。
 * Entra トークンを必要としないため microsoftAuthApi には依存しない。
 */
export const tenantInvitationApi = ApiBlueprint.make({
  name: 'tenant-invitation',
  params: define =>
    define(
      createApiFactory({
        api: tenantInvitationApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
        },
        factory: ({ discoveryApi, fetchApi }) =>
          new TenantInvitationClient({ discoveryApi, fetchApi }),
      }),
    ),
});

export const productListPage = SubPageBlueprint.make({
  name: 'product-list',
  params: {
    path: 'product-list',
    title: 'Products',
    routeRef: productListRouteRef,
    loader: () =>
      import('./features/ProductList/ProductList').then(m => <m.ProductList />),
  },
});

export const productDashboardPage = SubPageBlueprint.make({
  name: 'product-dashboard',
  params: {
    path: 'product-dashboard/:productId',
    title: 'Product Dashboard',
    routeRef: productDashboardRouteRef,
    loader: () =>
      import('./features/ProductDashboard/ProductDashboard').then(m => (
        <m.ProductDashboard />
      )),
  },
});

export const productCreatePage = SubPageBlueprint.make({
  name: 'product-create',
  params: {
    path: 'product-create',
    title: 'Add New Product',
    routeRef: productCreateRouteRef,
    loader: () =>
      import('./features/ProductCreate/ProductCreate').then(m => (
        <m.ProductCreate />
      )),
  },
});

export const tenantJoinRequestPage = SubPageBlueprint.make({
  name: 'tenant-join-request',
  params: {
    path: 'tenant-join-request',
    title: 'clz.scale Use Request',
    routeRef: tenantJoinRequestRouteRef,
    loader: () =>
      import('./features/TenantJoinRequest/TenantJoinRequest').then(m => (
        <m.TenantJoinRequest />
      )),
  },
});

export const runtimeEnvironmentPage = SubPageBlueprint.make({
  name: 'runtime-environment',
  params: {
    path: 'runtime-environment/:orchWorkspaceId/:runtimeId',
    title: 'Runtime Environment',
    routeRef: runtimeEnvironmentRouteRef,
    loader: () =>
      import('./features/RuntimeEnvironment/RuntimeEnvironment').then(m => (
        <m.RuntimeEnvironment />
      )),
  },
});

export const requestListPage = SubPageBlueprint.make({
  name: 'request-list',
  params: {
    path: 'request-list',
    title: 'Request List',
    routeRef: requestListRouteRef,
    loader: () =>
      import('./features/RequestList/RequestList').then(m => <m.RequestList />),
  },
});

export const productEditRequestPage = SubPageBlueprint.make({
  name: 'product-edit-request',
  params: {
    path: 'product-edit-request',
    title: 'Product Edit Request',
    routeRef: productEditRequestRouteRef,
    loader: () =>
      import('./features/ProductEditRequest/ProductEditRequest').then(m => (
        <m.ProductEditRequest />
      )),
  },
});

export const addRuntimeEnvironmentPage = SubPageBlueprint.make({
  name: 'add-runtime-environment',
  params: {
    path: 'add-runtime-environment',
    title: 'Add Runtime Environment',
    routeRef: addRuntimeEnvironmentRouteRef,
    loader: () =>
      import('./features/AddRuntimeEnvironment/AddRuntimeEnvironment').then(
        m => <m.AddRuntimeEnvironment />,
      ),
  },
});

export const idpControlBasePlugin = createFrontendPlugin({
  pluginId: 'idp-control-base',
  extensions: [
    page,
    navItemProducts,
    navItemTenantJoinRequest,
    bffApi,
    tenantInvitationApi,
    productListPage,
    productDashboardPage,
    productCreatePage,
    tenantJoinRequestPage,
    runtimeEnvironmentPage,
    addRuntimeEnvironmentPage,
    requestListPage,
    productEditRequestPage,
  ],
  routes: {
    root: rootRouteRef,
  },
});
